import React, {Component} from 'react'
import trans from "trans"
import _find from "lodash/find"

import {
    Theme,
    Section,
    Paginator,
    DataTable,
    DataHeader,
    DataRow,
    DataCell,
    DataBody,
    Grid,
    GridCell,
    css,
    Button,
    TextInput,
    Select,
    Checkbox,
    Icon,
    DateRange,
} from "@bdfx/mergado-ui-kit"

import {URL} from "app/config/api-config";
import { downloadRemoveWatermarkResults, getRemoveWatermarkDetectorResults, projectId, publishImageToWatermarkDetector, publishProductToWatermarkDetector } from "app/helpers/api";
import {AxiosResponse} from "axios";
import {WatermarkGroupType} from "../products/types";
import {stringToHash} from "../shape-edit/forms/helpers";
import store from 'store';
import { textToast } from 'app/helpers/toast_factory';
import { createToast } from 'actions';
import { ID } from 'app/types/app-types';
import { Spinner } from '@bdfx/mergado-ui-kit/lib';


export type RemoveWatermarkDetectorType = {
    id: number
    project_id: number
    image_id: number
    group_id: number
    group_hash: string
    image_hash: string
    effect_sensitivity: string
    status: string
    status_reason: string
    progress: string
    queued_at: string
    updated_at: string
}

export type ExtendedRemoveWatermarkDetectorType = RemoveWatermarkDetectorType & {
    image_url_hash: string
}

type TemplatesForWatermarkRemovalType = {
    template_id: number
    template_name: string
    group_id: number
}

interface Props {
    watermark_group: WatermarkGroupType
    templatesForWatermarkRemoval: TemplatesForWatermarkRemovalType[]
}

export type WMResultsFilterType = {
    imageId: number
    imageHash: string
    status: string
    progress: string
    from: string
    to: string
    currentHashOnly: boolean
}

const initFilter: WMResultsFilterType = {
    imageId: null,
    imageHash: null,
    status: null,
    progress: null,
    from: null,
    to: null,
    currentHashOnly: true,
}

interface State {
    page: number
    limit: number
    filter: WMResultsFilterType
    results: ExtendedRemoveWatermarkDetectorType[]
    totalResults: number
    exportFileName: string
    isExporting: boolean
    isLoadingResults: boolean
}

class WatermarkGroupsResults extends Component<Props,State> {
    constructor(props) {
        super(props)

        this.state = {
            page: 1,
            limit: 10,
            filter: initFilter,
            results: null,
            totalResults: null,
            exportFileName: null,
            isExporting: null,
            isLoadingResults: null,
        }
    }

    componentDidMount() {
        this.getResults(this.state.page, this.state.limit)
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (prevProps && prevProps.watermark_group.id !== this.props.watermark_group.id) {
            this.getResults(this.state.page, this.state.limit)
        }
    }

    getResults = (page, limit) => {
        const { watermark_group } = this.props
        const { filter } = this.state
        this.setState({ isLoadingResults: true })
        getRemoveWatermarkDetectorResults(watermark_group.id, page, limit, filter)
            .then((response: AxiosResponse<any>) => {
                this.setState({
                    results: response.data.results,
                    totalResults: response.data.totalResults,
                    page: response.data.page,
                    limit: response.data.limit,
                })
            })
            .catch(e => console.error('cannot get results for watermark detector', e))
            .finally(() => this.setState({ isLoadingResults: false }))
    }

    exportResults = () => {
        const {watermark_group} = this.props
        this.setState({isExporting: true})
        downloadRemoveWatermarkResults(watermark_group.id)
            .then((response: AxiosResponse<any>) => {
                this.setState({exportFileName: response.data.zipname})
            })
            .catch(e => console.error('cannot download results'))
            .finally(() => this.setState({isExporting: false}))
    }

    handleSendToWatermarkDetector = (watermarkGroupId: ID, imageId: ID) => {
        publishImageToWatermarkDetector(watermarkGroupId, imageId)
            .then((response: AxiosResponse<any>) => {
                store.dispatch(createToast(textToast('success', trans('published'))))
            })
            .catch(e => {
                store.dispatch(createToast(textToast('error', `cannot publish image to watermark detector: ${e.response?.data?.message}`)))
                console.error('cannot publish product', e.response)
            })
    }

    render() {
        const { results, page, limit, totalResults, exportFileName, isExporting, isLoadingResults, filter } = this.state
        const { watermark_group, templatesForWatermarkRemoval } = this.props

        const filters = <StyledFiltersGrid className='filters'>
            <Select
                label={'status'}
                options={['OK', 'NOT_OK', 'NOT_DETECTED'].map((o: string) => {
                    return {
                        label: trans(`status_${o}`),
                        value: `${o}`,
                    }
                })}
                value={filter.status}
                multiple={false}
                closeOnChange={true}
                onClear={() => this.setState({ filter: { ...filter, status: null } })}
                onChange={(value) => this.setState({ filter: { ...filter, status: value } })}
            />

            <Select
                label={'progress'}
                options={['done', 'queued'].map((o: string) => {
                    return {
                        label: `${o}`,
                        value: `${o}`,
                    }
                })}
                value={filter.progress}
                multiple={false}
                closeOnChange={true}
                onClear={() => this.setState({ filter: { ...filter, progress: null } })}
                onChange={(value) => this.setState({ filter: { ...filter, progress: value } })}
            />

            <TextInput
                label={'image id'}
                value={filter.imageId}
                onChange={(value) => this.setState({ filter: { ...filter, imageId: value } })}
                onClear={() => this.setState({ filter: { ...filter, imageId: null } })}
            />

            <TextInput
                label={'image hash'}
                value={filter.imageHash}
                onChange={(value) => this.setState({ filter: { ...filter, imageHash: value } })}
                onClear={() => this.setState({ filter: { ...filter, imageHash: null } })}
            />

            <DateRange
                /* labelFrom={<span>{trans('from_date')} <span className={'clickable clear-button'} onClick={() => this.setState({ filter: { ...filter, from: null } })}><Icon type={'close-light'} /></span></span >}
                labelTo={<span>{trans('to_date')} <span className={'clickable clear-button'} onClick={() => this.setState({ filter: { ...filter, to: null } })}><Icon type={'close-light'} /></span></span >} */
                labelFrom={trans('from_date')}
                labelTo={trans('to_date')}
                datetime={false}
                dateFrom={filter.from}
                dateTo={filter.to}
                onChangeFrom={(value) => this.setState({ filter: { ...filter, from: value } })}
                onChangeTo={(value) => this.setState({ filter: { ...filter, to: value } })}
                /* onClear={() => this.setState({ filter: { ...filter, from: null, to: null } })} */
            />

            <Checkbox
                label={`aktualni hash skupiny`}
                title={watermark_group.group_hash}
                value={filter.currentHashOnly}
                onChange={(value) => this.setState({ filter: { ...filter, currentHashOnly: value } })}
                onClear={() => this.setState({ filter: { ...filter, currentHashOnly: null } })}
            />

            <GridCell col={'1/-1'} style={{ textAlign: 'left !important' }}>
                <Button
                    type={'void'}
                    color={Theme.green}
                    icon={<Icon type={'filter'} text={trans('filter')} />}
                    onClick={() => {
                        this.setState({ page: 1 })
                        this.getResults(1, limit)
                    }}
                />
                <Button
                    type={'void'}
                    secondary={true}
                    color={Theme.orange}
                    icon={<Icon type={'close-light'} text={trans('reset_filters')} />}
                    onClick={() => this.setState({ filter: initFilter })}
                />
            </GridCell>
        </StyledFiltersGrid>

        if (!totalResults) {
            return <StyledSection color={Theme.steelblue} prefix={`${watermark_group.name}`} header={trans('no_results_yet')}>
                {filters}
            </StyledSection>
        }
        
        const paginator = totalResults > 1 ?
            <Paginator
                showFirstAndLast={true}
                showPrevAndNext={true}
                maxLinks={3}
                labelFirst={`««`}
                labelPrevious={`«`}
                labelNext={`»`}
                labelLast={`»»`}
                currentPage={page}
                lastPage={Math.ceil(totalResults/limit)}
                onPageChange={(e) => {
                    this.setState({page: e})
                    this.getResults(e, limit)
                }}
            /> : <span/>

            const t = templatesForWatermarkRemoval ? _find(templatesForWatermarkRemoval, (t: TemplatesForWatermarkRemovalType) => {
                return t.group_id === watermark_group.id
            }) : null            
            
        return (
            <StyledSection
                color={Theme.steelblue}
                prefix={`${watermark_group.name}`}
                header={t ? <React.Fragment>
                    {trans('results')}: <b>{t.template_name}</b> <b>({t.template_id})</b>
                    {isLoadingResults &&
                        <Spinner type={'bubbles'} />
                    }
                </React.Fragment> : trans('results')} suffix={<React.Fragment>
                    {exportFileName ?
                        <a href={`${URL.laravelBase}exports/${exportFileName}`} target={"_blank"} download>
                            <Button type="void" color={"green"} onClick={() => this.setState({ exportFileName: null })}>{trans("download")} {exportFileName}</Button>
                        </a> :
                        <Button
                            size={"tiny"}
                            label={`${trans("export_to_csv")} ${totalResults}`}
                            color="white"
                            simple={true}
                            icon={isExporting ? "spinner" : "cloud-download"}
                            onClick={(e) => this.exportResults()}
                        />
                    }
                </React.Fragment>}
            >
                {filters}            
                {paginator}

                {results?.length > 0 &&
                <DataTable style={{marginTop: '20px'}} id={'results'}>
                    <DataHeader>
                        <DataCell>image</DataCell>
                        <DataCell>image_id</DataCell>
                        <DataCell>image_hash</DataCell>
                        <DataCell>status</DataCell>
                        <DataCell>status_reason</DataCell>
                        <DataCell>effect_sensitivity</DataCell>
                        <DataCell>updated_at</DataCell>
                        <DataCell>queued_at</DataCell>
                        <DataCell>progress</DataCell>
                        <DataCell />
                    </DataHeader>
                    <DataBody>
                        {results.map(r => {
                            const key = `${r.id}_${r.project_id}_${r.image_id}`
                            const t = stringToHash(r.updated_at + r.image_hash + watermark_group.group_hash)
                            
                            const sourceimageUrl = `${URL.laravelBase}get-source-image/${watermark_group.project_id}/${r.image_hash}?t=${t}`
                            const wmremovedUrl = `${URL.laravelBase}get-autowmremoved/${watermark_group.project_id}/${r.image_url_hash}?t=${t}`
                            const wmremovedMaskUrl = `${URL.laravelBase}get-autowmremoved-mask/${watermark_group.project_id}/${r.image_url_hash}?t=${t}`

                            return <DataRow dataId={key} key={key}>
                                <DataCell>
                                    <StyledGrid cols={'repeat(3, 1fr)'} gap={'4px'}>
                                        <GridCell>
                                            <a href={sourceimageUrl} target={'_blank'}>
                                                <img src={sourceimageUrl} style={{maxWidth: '100px', maxHeight: '100px'}} />
                                            </a>
                                        </GridCell>
                                        <GridCell>
                                            <a href={wmremovedUrl} target={'_blank'}>
                                                <img src={wmremovedUrl} style={{maxWidth: '100px', maxHeight: '100px'}} />
                                            </a>
                                        </GridCell>
                                        <GridCell>
                                            <a href={wmremovedMaskUrl} target={'_blank'}>
                                                <img src={wmremovedMaskUrl} style={{maxWidth: '100px', maxHeight: '100px'}} />
                                            </a>
                                        </GridCell>
                                        <GridCell>{trans('source_image')}</GridCell>
                                        <GridCell>{trans('wmremoved_image')}</GridCell>
                                        <GridCell>{trans('watermark_mask')}</GridCell>
                                    </StyledGrid>
                                </DataCell>
                                <DataCell>{r.image_id}</DataCell>
                                <DataCell>{r.image_hash}</DataCell>
                                <DataCell>{r.status}</DataCell>
                                <DataCell>{r.status_reason}</DataCell>
                                <DataCell>{r.effect_sensitivity}</DataCell>
                                <DataCell>{r.updated_at}</DataCell>
                                <DataCell>{r.queued_at}</DataCell>
                                <DataCell>{r.progress}</DataCell>
                                <DataCell>
                                    <Button
                                        type={'void'}
                                        onClick={() => this.handleSendToWatermarkDetector(watermark_group.id, r.image_id)}
                                    >publish</Button>
                                </DataCell>
                            </DataRow>
                        })}
                    </DataBody>
                </DataTable>
                }

                {paginator}
            </StyledSection>
        )
    }
}

const StyledGrid = css(Grid)({
    ' .muk-gridcell': {
        fontSize: '10px',
        alignSelf: 'center',
        textAlign: 'center',
    }
})

const StyledFiltersGrid = css(Grid)({
    //gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gridGap: '10px',
    ' .muk-gridcell': {
        fontSize: '10px',
        alignSelf: 'center',
        textAlign: 'center',
    },
    ' .muk-daterange': {
        gridColumn: 'span 2',
    },
    ' .muk-form-group': {
        marginBottom: '0px',
    },
    ' .clear-button': {
        position: 'absolute',
        right: '0',
    },
    ' .muk-checkbox': {
        alignSelf: 'end',
        textAlign: 'center',
    }
})

const StyledSection = css(Section)({
    ' .m-header': {
        padding: '0 10px',
    }
})

const StyledCode = css('pre')({
    maxHeight: '250px',
    overflow: 'auto',
    borderRadius: '8px',
    border: '1px solid #dddddd',
    background: '#ffffff',
    code: {
        fontSize: '8px',
    }
})

export default WatermarkGroupsResults